import { defineStore } from 'pinia'
import {
  NabooContentAxis,
  NabooContentLearningModule
} from '@/core/domain/nabooContent/NabooContent.ts'
import { type Ref, ref } from 'vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import { FetchNabooContentUseCase } from '@/core/useCases/content/FetchNabooContentUseCase.ts'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import { FetchNabooContentModuleUseCase } from '@/core/useCases/content/FetchNabooContentModuleUseCase.ts'

export const useNabooContentStore = defineStore('NabooContent', () => {
  const listAxis: Ref<NabooContentAxis[]> = ref([])
  const currentLearningModule: Ref<NabooContentLearningModule | null> = ref(null)

  // USE CASES
  const fetchNabooContentUseCase = DependencyContainer.resolve(FetchNabooContentUseCase)
  const fetchNabooContentModuleUseCase = DependencyContainer.resolve(FetchNabooContentModuleUseCase)

  // STOREBASE
  const storeBase = createStoreBase(['fetchNabooContent', 'fetchNabooContentModule'])

  // ACTIONS
  async function fetchNabooContent() {
    try {
      storeBase.loading.value.fetchNabooContent = true
      storeBase.resetError()

      listAxis.value = await fetchNabooContentUseCase.execute()
    } catch (error) {
      return handleError(error, 'Une erreur est survenue lors de la récupération du contenu Naboo.')
    } finally {
      storeBase.loading.value.fetchNabooContent = false
    }
  }

  async function fetchNabooContentModule(learningCourseId: number, learningModuleId: number) {
    try {
      storeBase.loading.value.fetchNabooContentModule = true
      storeBase.resetError()

      currentLearningModule.value = await fetchNabooContentModuleUseCase.execute(
        learningModuleId,
        learningCourseId
      )
    } catch (error) {
      if (error instanceof Error) {
        error.message = 'Une erreur est survenue lors de la récupération du module.'
      }
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  function handleError(error: unknown, message: string) {
    if (!(error instanceof NabooError)) {
      error = new NabooError({
        message,
        status: 500,
        name: 'ModuleStoreError',
        code: 'MODULE_STORE_ERROR',
        originalError: error instanceof Error ? error : undefined
      })
    }

    storeBase.setError(error as NabooError)
    return Promise.reject(error)
  }

  return {
    listAxis,
    currentLearningModule,
    fetchNabooContent,
    fetchNabooContentModule
  }
})
